import React, { useState, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./CustomerCare.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faSearch,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContext } from "~/components/context/ToastContext";
import { BASE_CUSTOMER_CARE_URL } from "~/helper/apiService";

const cx = classNames.bind(styles);

function CareAgainPage() {
  const [loading, setLoading] = useState(false);
  const [caredList, setCaredList] = useState([]);
  const [caredMinDays, setCaredMinDays] = useState("60");
  const [limit, setLimit] = useState("100");
  const [totalCount, setTotalCount] = useState(0);
  const { showToast } = useContext(ToastContext);

  const fetchCaredList = async () => {
    setLoading(true);
    try {
      const caredMinDaysValue = caredMinDays === "" ? 60 : parseInt(caredMinDays) || 60;
      const limitValue = limit === "" ? 100 : parseInt(limit) || 100;
      const url = `${BASE_CUSTOMER_CARE_URL}/cared-long-ago?minDays=${caredMinDaysValue}${limitValue ? `&limit=${limitValue}` : ''}`;
      const response = await fetch(url, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        setCaredList(data.caredList || []);
        setTotalCount(data.total || 0);
      } else {
        showToast(data.error || "Lỗi khi lấy danh sách", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/refresh`, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        showToast("Đã refresh dữ liệu thành công", "success");
        fetchCaredList();
      } else {
        showToast(data.error || "Lỗi khi refresh", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcelCared = () => {
    const caredMinDaysValue = caredMinDays === "" ? 60 : parseInt(caredMinDays) || 60;
    const limitValue = limit === "" ? 100 : parseInt(limit) || 100;
    const url = `${BASE_CUSTOMER_CARE_URL}/export-excel-cared?minDays=${caredMinDaysValue}${limitValue ? `&limit=${limitValue}` : ''}`;
    window.open(url, "_blank");
  };

  const getSeverityClass = (days) => {
    if (days >= 90) return "severity-lost";
    if (days >= 60) return "severity-cold";
    if (days >= 30) return "severity-risk";
    return "severity-urgent";
  };

  const SkeletonRow = () => (
    <tr className={cx("tr")}>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td", "severity")}>
        <div className={cx("skeleton", "skeleton-badge")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text")}></div>
      </td>
    </tr>
  );

  const renderTable = () => {
    if (loading) {
      return (
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th")}>SĐT</th>
              <th className={cx("th")}>Tổng lượt đi</th>
              <th className={cx("th")}>Ngày đi gần nhất</th>
              <th className={cx("th")}>Số ngày chưa quay lại</th>
              <th className={cx("th")}>Mức độ</th>
              <th className={cx("th")}>Trạng thái CSKH</th>
              <th className={cx("th")}>Ngày CSKH gần nhất</th>
              <th className={cx("th")}>Số ngày từ CSKH gần nhất</th>
              <th className={cx("th")}>Số lần đã chăm sóc</th>
              <th className={cx("th")}>Nhân viên</th>
              <th className={cx("th")}>Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(10)].map((_, index) => (
              <SkeletonRow key={index} />
            ))}
          </tbody>
        </table>
      );
    }

    if (caredList.length === 0) {
      return <div className={cx("no-data")}>Không có dữ liệu</div>;
    }

    return (
      <>
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th")}>SĐT</th>
              <th className={cx("th")}>Tổng lượt đi</th>
              <th className={cx("th")}>Ngày đi gần nhất</th>
              <th className={cx("th")}>Số ngày chưa quay lại</th>
              <th className={cx("th")}>Mức độ</th>
              <th className={cx("th")}>Trạng thái CSKH</th>
              <th className={cx("th")}>Ngày CSKH gần nhất</th>
              <th className={cx("th")}>Số ngày từ CSKH gần nhất</th>
              <th className={cx("th")}>Số lần đã chăm sóc</th>
              <th className={cx("th")}>Nhân viên</th>
              <th className={cx("th")}>Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            {caredList.map((customer, index) => (
              <tr key={index} className={cx("tr")}>
                <td className={cx("td")}>{customer.phone}</td>
                <td className={cx("td")}>{customer.totalVisits}</td>
                <td className={cx("td")}>{customer.lastVisitDate}</td>
                <td className={cx("td")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</td>
                <td className={cx("td", "severity")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastCare || customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </td>
                <td className={cx("td")}>{customer.careStatus}</td>
                <td className={cx("td")}>{customer.lastCareDate || "-"}</td>
                <td className={cx("td")}>{customer.daysSinceLastCare !== null ? customer.daysSinceLastCare : "-"}</td>
                <td className={cx("td")}>{customer.careCount !== undefined ? customer.careCount : "-"}</td>
                <td className={cx("td")}>{customer.careStaff || "-"}</td>
                <td className={cx("td")}>{customer.careNote || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={cx("mobile-card-container")}>
          {caredList.map((customer, index) => (
            <div key={index} className={cx("mobile-card")}>
              <div className={cx("mobile-card-header")}>
                <div className={cx("mobile-card-title")}>{customer.phone}</div>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Tổng lượt đi:</span>
                <span className={cx("mobile-card-value")}>{customer.totalVisits}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Ngày đi gần nhất:</span>
                <span className={cx("mobile-card-value")}>{customer.lastVisitDate}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Số ngày chưa quay lại:</span>
                <span className={cx("mobile-card-value")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Mức độ:</span>
                <span className={cx("mobile-card-value")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastCare || customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Trạng thái CSKH:</span>
                <span className={cx("mobile-card-value")}>{customer.careStatus}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Ngày CSKH gần nhất:</span>
                <span className={cx("mobile-card-value")}>{customer.lastCareDate || "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Số ngày từ CSKH:</span>
                <span className={cx("mobile-card-value")}>{customer.daysSinceLastCare !== null ? customer.daysSinceLastCare : "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Số lần đã chăm sóc:</span>
                <span className={cx("mobile-card-value")}>{customer.careCount !== undefined ? customer.careCount : "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Nhân viên:</span>
                <span className={cx("mobile-card-value")}>{customer.careStaff || "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Ghi chú:</span>
                <span className={cx("mobile-card-value")}>{customer.careNote || "-"}</span>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={cx("customer-care")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>Chăm Sóc Lại Lần 2</h2>
        <div className={cx("actions")}>
          <button
            className={cx("button", "refresh-button")}
            onClick={handleRefresh}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      <div className={cx("tab-content")}>
        <div className={cx("filters")}>
          <div className={cx("filter-group")}>
            <label>Số ngày từ CSKH gần nhất ≥</label>
            <input  
              type="text"
              value={caredMinDays}
              onChange={(e) => setCaredMinDays(e.target.value)}
              className={cx("filter-input")}
            />
          </div>
          <div className={cx("filter-group")}>
            <label>Số lượng hiển thị</label>
            <input
              type="text"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className={cx("filter-input")}
            />
          </div>
          <button
            className={cx("button", "search-button")}
            onClick={fetchCaredList}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSearch} spin={loading} /> Bắt đầu tìm kiếm
          </button>
          <button
            className={cx("button", "export-button")}
            onClick={handleExportExcelCared}
            disabled={loading || caredList.length === 0}
          >
            <FontAwesomeIcon icon={faDownload} /> Tải Excel
          </button>
        </div>
        {totalCount > 0 && (
          <div className={cx("count-info")}>
            Hiển thị {caredList.length} / {totalCount} khách hàng
          </div>
        )}

        <div className={cx("table-container")}>
          {renderTable()}
        </div>
      </div>
    </div>
  );
}

export default CareAgainPage;
